import React, { useCallback, useMemo } from 'react';

import {
  List,
  Datagrid,
  TextField,
  DateField,
  SingleFieldList,
  ChipField,
  usePermissions,
  ReferenceArrayField,
  CreateButton,
  useRecordContext,
  useUpdate,
  useNotify,
  useRefresh,
  Button,
  TopToolbar,
} from 'react-admin';

import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { RemoveCircleOutline } from '@mui/icons-material';

import ApiKeyField from './Fields/ApiKeyField';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';

import hasAccess from '../../utilities/hasAccess';

import NoResults from '../layout/NoResults';
import Pagination from '../layout/Pagination';
import LinkField from '../layout/LinkField';
import actions, { Actions } from '../../constants/actions';
import resourceNames from '../../constants/resourceNames';
import useDialogStatus from '../../hooks/useDialogStatus';
import authenticatorTypes from '../../constants/authenticatorTypes';
import errorNotification from '../../utilities/errorNotification';

const DetachPermissionFromEntityButton = ({
  permissionId,
  permissionName,
}: {
  permissionId: string;
  permissionName: string;
}) => {
  const { open, openDialog, closeDialog } = useDialogStatus();

  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const onClickUnassignMember = useCallback(() => {
    if (!record) return;
    update(
      resourceNames.PERMISSION_ROLES,
      {
        id: `${permissionId}/detach`,
        data: { authenticators: [{ id: record.id, type: authenticatorTypes.API_KEY }] },
      },
      {
        onSuccess: () => {
          notify('ApiKey role removed', { type: 'info' });
          refresh();
        },
        onError: (e) => {
          notify(errorNotification(e), { type: 'error' });
        },
      },
    );
  }, [notify, permissionId, record, refresh, update]);

  if (!record) return null;
  return (
    <>
      <Dialog open={open} onClose={closeDialog} fullWidth>
        <DialogTitle fontWeight="bold">Remove api key</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={6}>
            <Box display="flex" gap={1}>
              <Typography>{`You are about to remove this api key from the ${permissionName} role:`}</Typography>
              <Typography fontWeight="bold">
                {`${record.id}`}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Typography>Are you sure you want to remove this api key?</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={onClickUnassignMember} variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
      <IconButton onClick={openDialog}>
        <RemoveCircleOutline />
      </IconButton>
    </>
  );
};

export const AssignedAPIKeysDatagrid = () => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Datagrid bulkActionButtons={false} sx={boldDataGridStyle} empty={<NoResults variant="h6" />}>
      {!hasAccess(permissions, actions.API_KEY_GET) && <TextField sortable={false} source="id" label="ID" />}
      {hasAccess(permissions, actions.API_KEY_GET) && <LinkField sortable={false} source="id" label="ID" />}
      <DateField sortable source="createdAt" showTime />
      <TextField sortable={false} source="clientId" label="Client ID" />
      {hasAccess(permissions, actions.API_KEY_UPDATE) && (
        <DetachPermissionFromEntityButton
          permissionId={record.id.toString()}
          permissionName={record.name}
        />
      )}
    </Datagrid>
  );
};

const ApiKeyList = () => {
  const { permissions } = usePermissions<Actions[]>();
  const canCreateAPIKey = useMemo(
    () => hasAccess(permissions, actions.PERMISSION_ROLE_LIST)
      && hasAccess(permissions, actions.API_KEY_UPDATE),
    [permissions],
  );
  return (
    <List
      sort={{ field: 'createdAt', order: 'DESC' }}
      empty={false}
      resource={resourceNames.API_KEYS}
      pagination={<Pagination disableEmptyText />}
      actions={(
        <TopToolbar>
          {hasAccess(permissions, actions.API_KEY_CREATE) && <CreateButton />}
        </TopToolbar>
      )}
      exporter={false}
    >
      <Card sx={{ margin: 0 }} variant="outlined">
        <Datagrid bulkActionButtons={false} sx={boldDataGridStyle} empty={<NoResults variant="h6" />}>
          {!hasAccess(permissions, actions.API_KEY_GET) && <TextField sortable={false} source="id" label="ID" />}
          {hasAccess(permissions, actions.API_KEY_GET) && <LinkField sortable={false} source="id" label="ID" />}
          <DateField sortable source="createdAt" showTime />
          <TextField sortable={false} source="clientId" label="Client ID" />
          {hasAccess(permissions, actions.API_KEY_GET_SECRET) && (<ApiKeyField label="Client Secret" />)}
          {canCreateAPIKey && (
            <ReferenceArrayField sortable={false} source="permissionRoleIds" label="Permission roles" reference={resourceNames.PERMISSION_ROLES}>
              <SingleFieldList linkType={false}>
                <ChipField source="name" />
              </SingleFieldList>
            </ReferenceArrayField>
          )}
        </Datagrid>
      </Card>
    </List>
  );
};

export default ApiKeyList;
